import type { ReactNode } from 'react';
import {
  Content,
  EmptyState,
  Header,
  LinkButton,
  Page,
  Progress,
} from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import { platformAdminViewPermission } from '@internal/plugin-rbac-common';

/**
 * Renders `children` only for a platform admin, and something honest for
 * everybody else.
 *
 * The permission is asked of the permission backend rather than derived from
 * the signed-in user's groups, for the same reason the sidebar does it that way
 * (`../nav/useIsPlatformAdmin.ts`): the frontend must not become a second
 * source of truth for who is an operator. `usePermission` is called here
 * directly instead of reusing that hook because the hook folds "still checking"
 * into "not allowed" - correct for a sidebar item that may simply stay hidden,
 * wrong for a whole page, where it would flash a refusal at an admin who is in
 * fact allowed.
 */
export const PlatformAdminOnly = ({ children }: { children: ReactNode }) => {
  const { loading, allowed } = usePermission({
    permission: platformAdminViewPermission,
  });

  if (allowed) {
    return <>{children}</>;
  }

  // The page shell is drawn in both remaining states so the header does not
  // arrive after the body: the check is a round trip to the permission backend
  // and on a cold session it is long enough to see.
  return (
    <Page themeId="home">
      <Header
        title="Catalog"
        subtitle="Every entity the platform knows about"
      />
      <Content>
        {loading ? (
          <Progress />
        ) : (
          /*
           * Not an error page: nothing failed, and a 403 screen invites a
           * support ticket about a page this user never needed. The catalog
           * answers "what exists on this platform", which is an operator
           * question; the answer to "where are my things" is My stacks, so
           * that is where the way out points.
           */
          <EmptyState
            missing="info"
            title="This page is for platform operators"
            description={
              'The catalog lists every entity on the platform, including other ' +
              'tenants. Your own environments, and the actions available on ' +
              'them, are on My stacks.'
            }
            action={
              <LinkButton to="/my-stacks" variant="contained" color="primary">
                Go to My stacks
              </LinkButton>
            }
          />
        )}
      </Content>
    </Page>
  );
};
